import React, { ForwardedRef, forwardRef, Ref, useRef } from 'react';
import clsx from 'clsx';
import { useFocusVisible } from 'react-aria/useFocusVisible';
import { AriaTabListProps, useTabList } from 'react-aria/useTabList';
import { filterDOMProps } from 'react-aria/filterDOMProps';
import { mergeProps } from 'react-aria/mergeProps';
import { useObjectRef } from 'react-aria/useObjectRef';
import { useTabListState } from 'react-stately/useTabListState';
import { CollectionChildren, Key } from '@react-types/shared';
import styles from '@danske/sapphire-css/components/segmentedControl/segmentedControl.module.css';

import { SegmentedTab } from './SegmentedTab';
import { TabPanelWrapper } from '../../Tabs/src/TabPanelWrapper';
import {
  SapphireStyleProps,
  useSapphireStyleProps,
  useThemeCheck,
} from '../../';
import {
  BaseComponentKeys,
  CollectionBaseKeys,
  GlobalDomAttributes,
  SingleSelectionSelectedKeyKeys,
  SingleSelectionSelectionChangeKeys,
} from '../../utils/types';
import useAnimatedSegment from '../../utils/useAnimatedSegment';

export interface SegmentedTabsProps<T>
  extends Omit<
      Pick<
        AriaTabListProps<T>,
        | 'children'
        | 'keyboardActivation'
        | 'isDisabled'
        | CollectionBaseKeys
        | SingleSelectionSelectionChangeKeys
        | SingleSelectionSelectedKeyKeys
        | BaseComponentKeys
      >,
      'selectedKey'
    >,
    GlobalDomAttributes,
    SapphireStyleProps {
  children: CollectionChildren<T>;
  /**
   * @default "md"
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Alignment of tabs' buttons group.
   * @default "left"
   */
  align?: 'left' | 'center' | 'right' | 'stretch';

  /**
   * Whether to keep tab panels mounted in the DOM after they've been visited.
   *
   * When false (default): Tab panels are unmounted when you switch away from them, losing their state.
   * When true: Tab panels are mounted when first visited and then kept in the DOM using CSS display: none,
   * preserving their state when you switch between tabs.
   *
   * @default false
   */
  keepMounted?: boolean;

  selectedKey?: Key | null;
}

function _SegmentedTabs<T extends object>(
  { selectedKey, ...otherProps }: SegmentedTabsProps<T>,
  forwardedRef: ForwardedRef<HTMLDivElement>
) {
  useThemeCheck();
  const props = {
    selectedKey: selectedKey === null ? undefined : selectedKey,
    ...otherProps,
  };
  const ref = useObjectRef(forwardedRef);
  const segmentedListRef = useRef<HTMLDivElement>(null);
  const activeElementRef = useRef<HTMLButtonElement>(null);
  const { isFocusVisible } = useFocusVisible();
  const { isDisabled, size = 'md', align = 'left' } = props;
  const { styleProps } = useSapphireStyleProps(props);
  const state = useTabListState(props);
  const { tabListProps } = useTabList(props, state, segmentedListRef);
  const domProps = filterDOMProps(props, { global: true });
  const { canAnimate, position } = useAnimatedSegment(
    {
      containerRef: segmentedListRef,
      activeElementRef,
      onComputeRequest: (element) => {
        const segmentedList = segmentedListRef.current;
        if (!segmentedList) return { width: 0, offsetLeft: 0 };
        return {
          width: element?.clientWidth ?? 0,
          offsetLeft: element?.offsetLeft ?? 0,
        };
      },
    },
    [state.selectedKey, align]
  );

  return (
    <>
      <div
        ref={ref}
        className={clsx(
          styleProps.className,
          styles['sapphire-segmented-control'],
          align === 'stretch'
            ? styles[`sapphire-segmented-control--stretch`]
            : [styles[`sapphire-segmented-control--align-${align}` as const]],
          {
            [styles['focus-visible']]: isFocusVisible,
            [styles['sapphire-segmented-control--lg']]: size === 'lg',
            [styles['sapphire-segmented-control--sm']]: size === 'sm',
            [styles['sapphire-segmented-control--manual-keyboard-activation']]:
              props.keyboardActivation === 'manual',
          }
        )}
        style={styleProps.style}
      >
        <div
          ref={segmentedListRef}
          {...mergeProps(tabListProps, domProps, {
            className: styles['sapphire-segmented-control__button-container'],
          })}
        >
          {Array.from(state.collection).map((item) => {
            return (
              <SegmentedTab
                key={item.key}
                ref={
                  item.key === state.selectedKey ? activeElementRef : undefined
                }
                isDisabled={isDisabled || state.disabledKeys?.has(item.key)}
                isActive={item.key === state.selectedKey}
                state={state}
                item={item}
              >
                {item.rendered}
              </SegmentedTab>
            );
          })}
          <span
            className={clsx(styles['sapphire-segmented-control__glider'], {
              [styles['sapphire-segmented-control__glider--with-transition']]:
                canAnimate,
            })}
            style={{
              width: position.width,
              left: position.offsetLeft,
            }}
          />
        </div>
      </div>
      <TabPanelWrapper state={state} keepMounted={props.keepMounted} />
    </>
  );
}

export const SegmentedTabs = forwardRef(_SegmentedTabs) as <T extends object>(
  props: SegmentedTabsProps<T> & { ref?: Ref<HTMLDivElement> }
) => React.ReactElement;
